import {
  VisaDocumentLiteModel,
  useGetVisaDocuments,
} from 'api-hooks/visa-document';
import { NavigationRoutes } from 'common/constants';
import notification from 'components/common/notification';
import QueryFilter from 'components/common/query-filter/query-filter';
import ListHeader from 'components/elements/list-header';
import { IColumn } from 'components/elements/table-component/table-component';
import { useApplyQueryFilter } from 'hooks/filter/use-apply-query-filter';
import { FilterHandleParentProvider } from 'hooks/filter/use-filter-handle-parent';
import useApplyQuerySort from 'hooks/use-apply-query-sort';
import useComposedQuery from 'hooks/use-composed-query';
import useDate from 'hooks/use-date';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useGetListParamsValue from 'hooks/use-get-list-params-value';
import { Container, TopContainer } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { NavigationProps } from 'types';

import VisaDocumentTable from './components/visa-document-table';

export type VisaDocumentListParams = null;

export default function VisaDocumentList(
  props: NavigationProps<NavigationRoutes.VisaDocumentList>,
) {
  const { queryPage } = useGetListParamsValue();
  const [page, setPage] = React.useState(queryPage);

  const formatDate = useDate();

  const { t } = useTranslation();
  const _columns = React.useMemo<IColumn<VisaDocumentLiteModel>[]>(
    () => [
      {
        header: t('common:created_at'),
        accessorKey: 'createdAt',
        minSize: 150,
        sortName: 'created_at',
        cell: ({ row }) =>
          row.original.createdAt
            ? formatDate(row.original.createdAt, 'dd MMM yyyy, HH:mm')
            : '-',
      },
      {
        header: t('common:name'),
        accessorKey: 'name',
        minSize: 250,
        sortName: 'name',
      },
      {
        header: t('common:created_by'),
        accessorKey: 'createdBy.name',
        minSize: 250,
        sortName: 'created_by',
        cell: ({ getValue }) => getValue() ?? '-',
      },
    ],
    [formatDate, t],
  );

  const {
    data,
    refetch,
    isLoading,
    isFetching,
    error,
    query,
    extras: [{ filters, setFilters }, { columns }],
  } = useComposedQuery(
    useGetVisaDocuments,
    {
      params: {
        page,
      },
    },
    useApplyQueryFilter((data: any) => {
      return data.filters;
    }),
    useApplyQuerySort((data: any) => {
      return data.sorts;
    }, _columns),
  );

  React.useEffect(() => {
    if (!!error && !!error.message) {
      notification.error({ message: error.message });
    }
  }, [error]);
  const permission = useGetCrudPermission('visa-documents');

  return (
    <FilterHandleParentProvider
      values={{
        filters,
        setFilters,
        page,
        setPage,
      }}
    >
      <Container>
        <ListHeader
          query={query}
          title={t('common:visa-document')}
          showAdd={permission.create}
          onRefresh={refetch}
          linkAdd={NavigationRoutes.VisaDocumentCreate}
        />
        <TopContainer>
          <QueryFilter filters={filters} />
        </TopContainer>
        <VisaDocumentTable
          visaDocuments={data?.data || []}
          loading={isLoading || isFetching}
          page={page}
          onPageChange={(page) => setPage(page)}
          meta={data?.meta}
          sortColumns={columns || []}
        />
      </Container>
    </FilterHandleParentProvider>
  );
}
